import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FreeMode, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import ElapsedTime from '../../../common/components/elapsed-time/ElapsedTime';
import { appSubscriptionService } from '../../../common/subscriptions/appSubscriptionService';
import { DispenseState } from '../../api/dispenseLogs/DispenseLog';
import { useGetOutstandingDispenseLogsQuery } from '../../api/dispenseLogs/dispenseLogsApiSlice';
import '../style.sass';
import './outstanding-dispense.sass';

import 'swiper/css/bundle';

const operatorsForLog = (items: { operatorName?: string }[]): string => {
  const names = Array.from(new Set(items.map((i) => i.operatorName).filter((n): n is string => !!n)));
  return names.length > 0 ? names.join(', ') : '-';
};

const stateLabel = (state: DispenseState): string => {
  switch (state) {
    case DispenseState.Pending:
      return 'Pending';
    case DispenseState.Preparing:
      return 'Preparing';
    case DispenseState.ReadyToServe:
      return 'Ready';
    case DispenseState.Served:
      return 'Served';
    default:
      return String(state);
  }
};

function OutstandingDispense() {
  const navigate = useNavigate();
  const { data: dispenseLogs, isLoading, refetch } = useGetOutstandingDispenseLogsQuery();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const logs = dispenseLogs || [];

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  useEffect(() => {
    const subs = [
      appSubscriptionService.dispenseLogCreatedObservable().subscribe(() => refetch()),
      appSubscriptionService.dispenseLogStateChangedObservable().subscribe(() => refetch()),
      appSubscriptionService.dispenseLogVoidedObservable().subscribe(() => refetch()),
      appSubscriptionService.dispenseLogVoidAcknowledgedObservable().subscribe(() => refetch()),
      appSubscriptionService.dispenseCompleteObservable().subscribe(() => refetch()),
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, [refetch]);

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') navigate(-1);
        }}
      >
        <div className="pos-modal pos-modal_role_outstanding-dispense">
          <div className="pos-modal__header">
            <h2 className="pos-modal__title pos-modal__title_role_outstanding-dispense">Outstanding Dispense</h2>
          </div>

          <div className="pos-modal__content">
            {isLoading && <div className="pos-modal__loading">Loading...</div>}

            {!isLoading && logs.length === 0 && <div className="pos-modal__empty">No outstanding dispense orders</div>}

            {!isLoading && logs.length > 0 && (
              <div className="pos-outstanding-dispense">
                <div className="pos-outstanding-dispense__scroll">
                  <Swiper
                    freeMode={true}
                    height={500}
                    slidesPerView="auto"
                    spaceBetween={0}
                    mousewheel={true}
                    scrollbar={true}
                    direction="vertical"
                    pagination={{ clickable: true }}
                    modules={[FreeMode, Pagination]}
                  >
                    <SwiperSlide>
                      <div className="pos-outstanding-dispense__content">
                        <table className="pos-outstanding-dispense__table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Transaction #</th>
                              <th>Table #</th>
                              <th>Device</th>
                              <th>Operator</th>
                              <th>State</th>
                              <th>Waiting</th>
                              <th>Items</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logs.map((log) => {
                              const isExpanded = expandedRows.has(log.id);
                              const stateClass = `pos-outstanding-dispense__state pos-outstanding-dispense__state_role_${stateLabel(
                                log.state,
                              ).toLowerCase()}`;
                              return (
                                <>
                                  <tr
                                    key={log.id}
                                    className={`pos-outstanding-dispense__row${log.isVoid ? ' pos-outstanding-dispense__row_void' : ''}`}
                                  >
                                    <td>
                                      <button
                                        className="pos-outstanding-dispense__expand-btn"
                                        onClick={() => toggleRow(log.id)}
                                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                      >
                                        {isExpanded ? '▼' : '▶'}
                                      </button>
                                    </td>
                                    <td>{log.transactionNumber}</td>
                                    <td>{log.tableNumber || '-'}</td>
                                    <td>{log.orderDeviceName}</td>
                                    <td>{operatorsForLog(log.items)}</td>
                                    <td>
                                      {log.isVoid ? (
                                        <span className="pos-outstanding-dispense__void-badge">VOID</span>
                                      ) : (
                                        <span className={stateClass}>{stateLabel(log.state)}</span>
                                      )}
                                    </td>
                                    <td>
                                      <ElapsedTime since={log.dateCreated} />
                                    </td>
                                    <td>{log.items.length}</td>
                                  </tr>
                                  {isExpanded && (
                                    <tr key={`${log.id}-items`} className="pos-outstanding-dispense__items-row">
                                      <td colSpan={8}>
                                        <div className="pos-outstanding-dispense__items">
                                          <h4>Items:</h4>
                                          <ul className="pos-outstanding-dispense__items-list">
                                            {log.items.map((item) => (
                                              <li
                                                key={item.id}
                                                className={`pos-outstanding-dispense__item${
                                                  item.isVoid ? ' pos-outstanding-dispense__item_void' : ''
                                                }`}
                                              >
                                                <div className="pos-outstanding-dispense__item-main">
                                                  <span className="pos-outstanding-dispense__item-qty">{item.quantity}x</span>
                                                  <span className="pos-outstanding-dispense__item-desc">{item.description}</span>
                                                  {item.isVoid && <span className="pos-outstanding-dispense__item-void-label">VOID</span>}
                                                </div>
                                                {item.childItems && item.childItems.length > 0 && (
                                                  <ul className="pos-outstanding-dispense__child-items">
                                                    {item.childItems.map((childItem, index) => (
                                                      <li
                                                        key={`${item.id}-child-${index}`}
                                                        className="pos-outstanding-dispense__child-item"
                                                      >
                                                        <span className="pos-outstanding-dispense__child-desc">
                                                          {childItem.description}
                                                        </span>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutstandingDispense;
